<template>
  <grid-page-loading-error
    v-if="error !== null && !loading"
    @retry="init"
  />
  <div
    v-else-if="gridPageTileIframe === null || loading"
    class="_spinner"
  >
    <m-spinner />
  </div>
  <m-content
    v-else
    :padding-top="gridPageTile.hideBorder ? -1 : 0"
    :padding-x="gridPageTile.hideBorder ? -1 : 9"
    :padding-bottom="gridPageTile.hideBorder ? -1 : 6"
    class="grid-page-tile-iframe"
  >
    <m-dropdown
      v-model:value="showSettings"
      :title="$t('general.actions')"
      block
      :relocate-key="relocateKey"
      @hide="hide"
    >
      <div
        v-if="gridPageTileIframe.url === ''"
        class="_button-container"
      >
        <div class="_button">
          <div class="_icon">
            <m-icon type="embed-icon" />
          </div>
          <div class="_text">
            {{ $t('gridPageTileIframe.empty') }}
          </div>
        </div>
      </div>
      <template #overlay>
        <m-card
          small-padding
          class="_card"
        >
          <m-form-item>
            <m-input
              v-model:value="url"
              :placeholder="$t('gridPageTileIframe.placeholder')"
              auto-focus
              :has-error="!validUrl"
              full-width
            />
          </m-form-item>
          <m-btn
            block
            color="primary"
            :disabled="!validUrl"
            @click="saveUrl"
          >
            {{ $t('gridPageTileIframe.embedLink') }}
          </m-btn>
          <div
            v-if="nonEmbeddableProvider !== null"
            class="_callout"
          >
            <m-icon
              type="info-circle"
              size="16"
              class="_callout-icon"
            />
            <div class="_callout-text">
              {{ $t('gridPageTileIframe.embedProviderWarning', { provider: nonEmbeddableProvider }) }}
            </div>
          </div>
          <div class="_hint">
            {{ $t('gridPageTileIframe.hint') }}
          </div>
        </m-card>
      </template>
    </m-dropdown>
    <div
      v-if="gridPageTileIframe.url !== '' && savedNonEmbeddableProvider !== null"
      class="_blocked"
    >
      <div class="_blocked-icon">
        <m-icon
          type="embed-icon"
          size="22"
        />
      </div>
      <div class="_blocked-title">
        {{ $t('gridPageTileIframe.embedBlockedTitle', { provider: savedNonEmbeddableProvider }) }}
      </div>
      <div class="_blocked-text">
        {{ $t('gridPageTileIframe.embedProviderWarning', { provider: savedNonEmbeddableProvider }) }}
      </div>
    </div>
    <div
      v-else-if="gridPageTileIframe.url !== ''"
      ref="container"
      class="_iframe-wrapper"
    >
      <iframe
        ref="iframe"
        :width="dimensions.width"
        :height="dimensions.height"
        :src="gridPageTileIframe.url"
        frameborder="0"
        style="border:0"
        allowfullscreen
        sandbox="allow-presentation allow-storage-access-by-user-activation allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      />
    </div>
  </m-content>
</template>

<script setup>
import GridPageLoadingError from '@/components/custom-grid/GridPageLoadingError.vue';
import useGridPage from '@/composables/grid-page/grid-page';
import useInlineEditing from '@/composables/inline-editing';
import { computed, onBeforeUnmount, onMounted, ref, toRef, watch } from 'vue';
import { logCatch } from '@/lib/logger/logger';

const props = defineProps({
  gridPageTile: { type: Object, required: true },
  readOnly: { type: Boolean, default: false },
  showReplaceMenu: { type: Boolean, default: false },
});

const url = ref('');

const relocateKey = ref(0);
const showSettings = ref(false);

const validUrl = computed(() => {
  if (url.value === '') {
    return true;
  }
  const regex = /^(https?:\/\/)?((([a-z\d]([a-z\d-]*[a-z\d])*)\.)+[a-z]{2,})/;
  return regex.test(url.value);
});

// Providers that block cross-origin framing of their normal app/share URL but
// expose a dedicated embed link. The block is cross-origin so we can't detect
// it at runtime; instead `matches` recognises the blocked URL shapes while
// embed links (different host/path) fall through via `isEmbedLink`. Limited to
// providers with an unambiguous, well-established embed format - providers that
// frame fine (Typeform, Calendly, ...) are intentionally absent.
const nonEmbeddableProviders = [
  {
    name: 'Power BI',
    matches: (u) => u.hostname === 'app.powerbi.com',
    isEmbedLink: (u) => /^\/(view|reportEmbed)/i.test(u.pathname),
  },
  {
    name: 'Looker Studio',
    matches: (u) => u.hostname === 'lookerstudio.google.com' || u.hostname === 'datastudio.google.com',
    isEmbedLink: (u) => u.pathname.startsWith('/embed/'),
  },
  {
    name: 'Tableau',
    matches: (u) => u.hostname === 'public.tableau.com',
    isEmbedLink: (u) => u.pathname.startsWith('/views/'),
  },
  {
    name: 'Google',
    matches: (u) => u.hostname === 'docs.google.com' && /^\/(document|spreadsheets|presentation)\//.test(u.pathname),
    // "Publish to web" links use the /d/e/<key>/ form with a pubhtml (Sheets),
    // pub (Docs) or embed (Slides) endpoint; /preview uses the raw file id.
    // Anything else (e.g. an /edit editor link) won't frame.
    isEmbedLink: (u) => u.pathname.includes('/d/e/') || /\/(pubhtml|pub|preview|embed)(?:\/|$)/i.test(u.pathname),
  },
  {
    name: 'Airtable',
    matches: (u) => (u.hostname === 'airtable.com' || u.hostname === 'www.airtable.com') && /\/(app|shr|tbl)[a-zA-Z0-9]/.test(u.pathname),
    isEmbedLink: (u) => u.pathname.startsWith('/embed/'),
  },
  {
    name: 'Figma',
    matches: (u) => (u.hostname === 'figma.com' || u.hostname === 'www.figma.com') && /^\/(file|design|board|proto)\//.test(u.pathname),
    isEmbedLink: () => false, // embed link lives on embed.figma.com
  },
  {
    name: 'Miro',
    matches: (u) => (u.hostname === 'miro.com' || u.hostname === 'www.miro.com') && u.pathname.startsWith('/app/board/'),
    isEmbedLink: () => false, // embed link uses /app/live-embed/
  },
  {
    name: 'Notion',
    matches: (u) => u.hostname === 'notion.so' || u.hostname === 'www.notion.so' || u.hostname.endsWith('.notion.site'),
    isEmbedLink: (u) => u.pathname.startsWith('/ebd/'),
  },
  {
    name: 'YouTube',
    matches: (u) => ['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtu.be'].includes(u.hostname)
      && (u.hostname === 'youtu.be' || /^\/(watch|shorts|embed)/.test(u.pathname)),
    isEmbedLink: (u) => u.hostname !== 'youtu.be' && u.pathname.startsWith('/embed/'),
  },
  {
    name: 'Vimeo',
    matches: (u) => (u.hostname === 'vimeo.com' || u.hostname === 'www.vimeo.com') && /\/\d{6,}/.test(u.pathname),
    isEmbedLink: () => false, // embed link lives on player.vimeo.com
  },
  {
    name: 'Loom',
    matches: (u) => (u.hostname === 'loom.com' || u.hostname === 'www.loom.com') && u.pathname.startsWith('/share/'),
    isEmbedLink: () => false, // embed link uses /embed/
  },
];

const blockingProvider = (rawUrl) => {
  const val = (rawUrl ?? '').trim();
  if (val === '' || val.startsWith('<iframe')) {
    return null;
  }
  let parsed;
  try {
    parsed = new URL(val.startsWith('http') ? val : `https://${val}`);
  } catch {
    return null;
  }
  const provider = nonEmbeddableProviders.find((p) => p.matches(parsed));
  if (provider === undefined || provider.isEmbedLink(parsed)) {
    return null;
  }
  return provider.name;
};

// While editing: warn about the URL currently typed into the input.
const nonEmbeddableProvider = computed(() => blockingProvider(url.value));

watch(url, (val) => {
  if (!val.startsWith('<iframe')) {
    return;
  }

  const tempElement = document.createElement('div');
  tempElement.innerHTML = val;

  const iframe = tempElement.querySelector('iframe');

  if (iframe !== null) {
    url.value = iframe.getAttribute('src');
  }
});

const emit = defineEmits(['close-replace-menu']);

const inlineEditingSvc = useInlineEditing();
const autoOpen = inlineEditingSvc.isInlineEditing(props.gridPageTile.uid);
watch(autoOpen, (val) => {
  if (val) {
    showSettings.value = true;
    setTimeout(() => {
      relocateKey.value += 1;
    }, 0);
  }
}, { immediate: true });

const showReplaceMenu = toRef(props, 'showReplaceMenu');
watch(showReplaceMenu, (val) => {
  if (val) {
    showSettings.value = true;
  }
});

const hide = () => {
  inlineEditingSvc.reset();
  emit('close-replace-menu');
};

const saveUrl = () => {
  gridPageSvc.updateGridPageTileIframe({ uid: gridPageTileIframe.value.uid, url: url.value }).then(() => {
    showSettings.value = false;
  });
};

const gridPageSvc = useGridPage();

const gridPageTileIframe = computed(() => {
  const res = gridPageSvc.gridPageTileIframe.value.find((e) => e.tile.uid === props.gridPageTile.uid);
  if (res === undefined) {
    return null;
  }

  return res;
});

// When rendered, a saved link that a provider blocks from framing would just
// show a blank tile - surface the reason in place instead.
const savedNonEmbeddableProvider = computed(() => blockingProvider(gridPageTileIframe.value?.url));

const dimensions = ref({});
const container = ref(null);

const setDimensions = () => {
  if (container.value === null) {
    return;
  }
  dimensions.value = container.value.getBoundingClientRect();
};

const resizeObserver = new ResizeObserver(setDimensions);

onMounted(() => {
  setDimensions();
});

watch(container, () => {
  if (container.value === null) {
    return;
  }
  resizeObserver.disconnect();
  resizeObserver.observe(container.value);
});

onBeforeUnmount(() => {
  resizeObserver.disconnect();
  inlineEditingSvc.reset();
});

const error = ref(null);
const loading = ref(false);
const init = () => {
  loading.value = true;
  error.value = null;
  gridPageSvc.queryGridPageTileIframeByTile(props.gridPageTile)
    .then(() => {
      url.value = gridPageTileIframe.value.url;
    })
    .catch(logCatch((e) => {
      error.value = e;
    }))
    .finally(() => {
      loading.value = false;
    });
};

init();

</script>

<style
    scoped
    lang="scss"
    type="text/scss"
>
._spinner {
  height: var(--viewport-height-100);
  display: flex;
  justify-content: center;
  align-items: center;
}

.grid-page-tile-iframe {
  overflow: hidden;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ._iframe-wrapper {
    width: 100%;
    height: 100%;
  }

  ._blocked {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: .4rem;
    padding: 2rem;
    max-width: 36rem;
    margin: 0 auto;

    ._blocked-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 4rem;
      height: 4rem;
      margin-bottom: .4rem;
      border-radius: 50%;
      color: map_get($grey, 'darken-1');
      background: map_get($grey, 'lighten-5');
    }

    ._blocked-title {
      font-weight: $font-weight-medium;
      color: $font-color-primary;
    }

    ._blocked-text {
      color: $font-color-secondary;
      font-size: $font-size-2;
      line-height: 1.4;
    }
  }

  ._button-container {
    display: flex;
    width: 100%;
    justify-content: center;
  }

  ._button {
    border-radius: $default-border-radius;
    color: map_get($grey, 'darken-1');
    background: map_get($grey, 'lighten-5');
    padding: 1rem;
    display: flex;
    align-items: center;
    cursor: pointer;

    &:hover {
      background: map_get($grey, 'lighten-3');
    }

    ._icon {
      margin-right: 1.2rem;
    }
  }
}

._card {
  min-width: 30rem;
  max-width: 34rem;

  ._hint {
    margin-top: .6rem;
    color: $font-color-secondary;
    font-size: $font-size-2;
  }

  ._callout {
    margin-top: .8rem;
    display: flex;
    align-items: flex-start;
    gap: .6rem;
    padding: .8rem 1rem;
    border-radius: $default-border-radius;
    background: map_get($yellow, 'lighten-6');
    color: map_get($yellow, 'darken-3');
    font-size: $font-size-2;
    line-height: 1.4;

    ._callout-icon {
      flex-shrink: 0;
      margin-top: .1rem;
      color: $warning-color;
    }
  }
}
</style>
